import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiStar, FiHome, FiCalendar, FiShield, FiWifi, FiMapPin } from 'react-icons/fi';
import { BsEvStation, BsCarFront } from 'react-icons/bs';
import { MdOutlineBedroomParent, MdOutlineBathtub } from 'react-icons/md';
import SearchBox from '../components/SearchBox';
import ApartmentCard from '../components/ApartmentCard';
import api from '../services/api';

const Home = () => {
  const [featuredApartments, setFeaturedApartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apartments = await api.getFeaturedApartments();
        setFeaturedApartments(apartments);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const styles = {
    hero: {
      minHeight: '100vh',
      background: 'linear-gradient(rgba(26, 58, 92, 0.6), rgba(26, 58, 92, 0.7)), url("/images/hero/hero-living.jpg") center/cover',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '120px 24px 80px',
      position: 'relative'
    },
    heroContent: {
      maxWidth: '900px',
      textAlign: 'center',
      color: 'white'
    },
    heroBrand: {
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: '400',
      letterSpacing: '0.3em',
      marginBottom: '8px',
      textTransform: 'uppercase',
      color: 'white'
    },
    heroTitle: {
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      fontWeight: '700',
      marginBottom: '16px',
      lineHeight: '1.2',
      fontFamily: "'Playfair Display', Georgia, serif"
    },
    heroSubtitle: {
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      color: 'white',
      marginBottom: '40px',
      maxWidth: '600px',
      margin: '0 auto 40px'
    },
    promoBanner: {
      display: 'inline-block',
      background: '#d4a5a5',
      color: '#1a3a5c',
      padding: '12px 24px',
      borderRadius: '4px',
      fontWeight: '600',
      marginBottom: '24px',
      fontSize: '1rem'
    },
    section: {
      padding: '80px 24px'
    },
    container: {
      maxWidth: '1280px',
      margin: '0 auto'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    sectionTitle: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: '#1a3a5c'
    },
    viewAll: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#1a3a5c',
      textDecoration: 'none',
      fontWeight: '500'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '24px'
    },
    locationGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '16px'
    },
    locationCard: {
      position: 'relative',
      borderRadius: '16px',
      overflow: 'hidden',
      aspectRatio: '1',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    locationImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease'
    },
    locationOverlay: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      padding: '20px',
      background: 'linear-gradient(transparent, rgba(26, 58, 92, 0.9))',
      color: 'white'
    },
    locationName: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '4px'
    },
    locationCount: {
      fontSize: '0.875rem',
      opacity: '0.9'
    },
    targetAudience: {
      background: '#1a3a5c',
      padding: '60px 24px',
      color: 'white'
    },
    audienceGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '32px',
      textAlign: 'center'
    },
    audienceItem: {
      padding: '24px'
    },
    audienceTitle: {
      fontSize: '1.125rem',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#d4a5a5'
    },
    audienceText: {
      color: 'rgba(255,255,255,0.9)',
      fontSize: '0.9375rem',
      lineHeight: '1.6'
    },
    features: {
      background: '#f7f7f7',
      padding: '80px 24px'
    },
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '32px'
    },
    featureCard: {
      textAlign: 'center',
      padding: '24px',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    },
    featureIcon: {
      width: '56px',
      height: '56px',
      background: '#1a3a5c',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      color: 'white',
      fontSize: '1.5rem'
    },
    featureTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1a3a5c'
    },
    whyUs: {
      padding: '80px 24px'
    },
    whyUsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '40px'
    },
    whyUsCard: {
      textAlign: 'center'
    },
    whyUsIcon: {
      width: '64px',
      height: '64px',
      background: '#d4a5a5',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      color: '#1a3a5c',
      fontSize: '1.75rem'
    },
    whyUsTitle: {
      fontSize: '1.125rem',
      fontWeight: '600',
      marginBottom: '12px',
      color: '#1a3a5c'
    },
    whyUsText: {
      color: '#555',
      lineHeight: '1.6'
    },
    cta: {
      background: 'linear-gradient(135deg, #1a3a5c 0%, #2c5282 100%)',
      padding: '80px 24px',
      textAlign: 'center',
      color: 'white'
    },
    ctaTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '16px'
    },
    ctaText: {
      fontSize: '1.125rem',
      color: 'rgba(255,255,255,0.95)',
      marginBottom: '32px',
      maxWidth: '600px',
      margin: '0 auto 32px'
    },
    ctaBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: '#d4a5a5',
      color: '#1a3a5c',
      padding: '16px 32px',
      borderRadius: '8px',
      textDecoration: 'none',
      fontWeight: '600',
      fontSize: '1rem',
      transition: 'all 0.2s ease'
    },
    contactInfo: {
      marginTop: '32px',
      fontSize: '1rem'
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <p style={styles.heroBrand}>Kasbia Stays</p>
          <h1 style={styles.heroTitle}>
            Your Home Away From Home
          </h1>
          <p style={styles.heroSubtitle}>
            Premium serviced accommodation in Gravesend. Perfect for contractors,
            re-locaters & short stay holidays. Flexible bookings to suit your needs.
          </p>
          <div style={styles.promoBanner}>
            10% OFF DIRECT BOOKINGS
          </div>
          <SearchBox variant="hero" />
        </div>
      </section>

      {/* Property Features Strip */}
      <section style={styles.features}>
        <div style={styles.container}>
          <h2 style={{...styles.sectionTitle, textAlign: 'center', marginBottom: '40px'}}>
            Property Features
          </h2>
          <div style={styles.featureGrid}>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <MdOutlineBedroomParent />
              </div>
              <h3 style={styles.featureTitle}>3 Bedrooms</h3>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <MdOutlineBathtub />
              </div>
              <h3 style={styles.featureTitle}>2 Bathrooms</h3>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <BsCarFront />
              </div>
              <h3 style={styles.featureTitle}>Private Parking</h3>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <BsEvStation />
              </div>
              <h3 style={styles.featureTitle}>Free EV Charger</h3>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <FiWifi />
              </div>
              <h3 style={styles.featureTitle}>Free WiFi</h3>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <FiHome />
              </div>
              <h3 style={styles.featureTitle}>Garden</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section style={styles.targetAudience}>
        <div style={styles.container}>
          <h2 style={{fontSize: '1.75rem', fontWeight: '700', textAlign: 'center', marginBottom: '40px'}}>
            Perfect For
          </h2>
          <div style={styles.audienceGrid}>
            <div style={styles.audienceItem}>
              <h3 style={styles.audienceTitle}>Contractors</h3>
              <p style={styles.audienceText}>
                Comfortable stays for working professionals with all amenities included
              </p>
            </div>
            <div style={styles.audienceItem}>
              <h3 style={styles.audienceTitle}>Re-locaters</h3>
              <p style={styles.audienceText}>
                Ideal temporary home while you settle into your new area
              </p>
            </div>
            <div style={styles.audienceItem}>
              <h3 style={styles.audienceTitle}>Short Stay Holidays</h3>
              <p style={styles.audienceText}>
                A home away from home for families and groups up to 8 people
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Apartments */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Our Accommodation</h2>
            <Link to="/apartments" style={styles.viewAll}>
              View all <FiArrowRight />
            </Link>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : (
            <div style={styles.grid}>
              {featuredApartments.map(apartment => (
                <ApartmentCard key={apartment._id} apartment={apartment} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={styles.whyUs}>
        <div style={styles.container}>
          <h2 style={{...styles.sectionTitle, textAlign: 'center', marginBottom: '48px'}}>
            Why Choose Kasbia Stays
          </h2>

          <div style={styles.whyUsGrid}>
            <div style={styles.whyUsCard}>
              <div style={styles.whyUsIcon}>
                <FiMapPin />
              </div>
              <h3 style={styles.whyUsTitle}>Great Location</h3>
              <p style={styles.whyUsText}>
                Ideally situated in Gravesend with excellent transport links to London and surrounding areas.
              </p>
            </div>

            <div style={styles.whyUsCard}>
              <div style={styles.whyUsIcon}>
                <FiCalendar />
              </div>
              <h3 style={styles.whyUsTitle}>Flexible Bookings</h3>
              <p style={styles.whyUsText}>
                Book for days, weeks, or months. We accommodate your schedule with ease.
              </p>
            </div>

            <div style={styles.whyUsCard}>
              <div style={styles.whyUsIcon}>
                <FiShield />
              </div>
              <h3 style={styles.whyUsTitle}>All-Inclusive</h3>
              <p style={styles.whyUsText}>
                WiFi, parking, EV charging, and all utilities included. No hidden costs.
              </p>
            </div>

            <div style={styles.whyUsCard}>
              <div style={styles.whyUsIcon}>
                <FiStar />
              </div>
              <h3 style={styles.whyUsTitle}>Home Comforts</h3>
              <p style={styles.whyUsText}>
                Fully furnished with living room, kitchen, garden - everything you need to feel at home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.cta}>
        <div style={styles.container}>
          <h2 style={styles.ctaTitle}>Ready to Book Your Stay?</h2>
          <p style={styles.ctaText}>
            Experience premium accommodation in Gravesend. Your home away from home awaits.
          </p>
          <Link to="/apartments" style={styles.ctaBtn}>
            Book Now & Save 10% <FiArrowRight />
          </Link>
          <div style={styles.contactInfo}>
            <p style={{marginBottom: '8px', color: 'rgba(255,255,255,0.9)'}}>Or contact us directly:</p>
            <p style={{fontWeight: '600', color: 'white'}}>
              07577 952535 | 07715 512267 | 07853 083370
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
